import React, {JSX, ReactElement, ReactNode} from "react";
import { useParams } from "react-router-dom";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode"; // 👈 Install if not already: npm install jwt-decode
import Register from "./Components/user/ Register";
import Login from "./Components/auth/ Login";
import Dashboard from "./Components/user/Dashboard";
import ProfileSettings from "./Components/user/ ProfileSettings";
import PricePlans from "./Components/user/PricingPlans";
import ChatBotEmbed from "./Components/user/ChatbotEmbed";
import SidebarLayout from "./Components/shared/SidebarLayout";
import AdminLogin from "./Components/admin/AdminLogin";
import AdminDashboard from "./Components/admin/AdminDashboard";
import ManageSubscriptions from "./Components/admin/ManageSubscriptions";
import ManageRestaurants from "./Components/admin/ManageRestaurants";
import AdminUsers from "./Components/admin/AdminUsers";
import Chatbot from "./Components/user/Chatbot"; // ✅ Import your chatbot UI
import SubscriptionSuccess from "./Components/user/SubscriptionSuccess";
import SubscriptionCancel from "./Components/user/SubscriptionCancel";
import ChatbotThemeCustomizer from "./Components/user/ChatbotThemeCustomizer";
import ChatAnalyticsDashboard from "./Components/user/ChatAnalyticsDashboard";
import MenuList from "./Components/user/MenuList";
import LandingPage from "./Components/pages/LandingPage";
import Pricing from "./Components/pages/Pricing";
import Contact from "./Components/pages/Contact";
import AdminApiDocs from "./Components/pages/AdminApiDocs";
import ForgotPassword from "./Components/auth/ForgotPassword";
import ResetPassword from "./Components/auth/ResetPassword";
import ActivateAccount from "./Components/pages/ActivateAccount";
import AdminNewsletter from "./Components/admin/Newsletter";
import AdminChatLimit from "./Components/admin/AdminChatLimit";
import AdminChatResetLogs from "./Components/admin/AdminChatResetLogs";

import "./App.css";

// Protected Route Component
const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
    const token = localStorage.getItem("access_token");

    if (!token) {
        console.warn("No token found, redirecting to login...");
        return <Navigate to="/login" replace />;
    }

    try {
        const decoded: any = jwtDecode(token);
        if (decoded.exp * 1000 < Date.now()) {
            console.warn("Token expired, redirecting to login...");
            return <Navigate to="/login" replace />;
        }
        return children;
    } catch (error) {
        console.error("Invalid token:", error);
        return <Navigate to="/login" replace />;
    }
};


interface PrivateRouteProps {
    element: ReactElement;
}

const PrivateRoute = ({ element }: PrivateRouteProps) => {
    const token = localStorage.getItem("admin_access_token");
    return token ? element : <Navigate to="/admin/login" />;
};

// const ChatbotWrapper = () => {
//     const queryParams = new URLSearchParams(window.location.search);
//     const restaurantId = queryParams.get("restaurant_id") || "";
//     return <Chatbot restaurantId={restaurantId} />;
// };

const ChatbotWrapper = () => {
    const { slug } = useParams();
    const queryParams = new URLSearchParams(window.location.search);
    const restaurantId = queryParams.get("restaurant_id") || "";

    // Prefer slug if available, fallback to restaurantId
    return slug ? (
        <Chatbot restaurantSlug={slug} />
    ) : (
        <Chatbot restaurantId={restaurantId} />
    );
};


function App() {
    const restaurantId = localStorage.getItem("restaurant_id") || "";
    // console.log("restaurantId from App.tsx: ", restaurantId);
    return (
        <Router>
            <Routes>
                <Route path="/" element={<LandingPage />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/contact" element={<Contact />} />
                {/*<Route path="/" element={<Navigate to="/dashboard" replace />} />*/}

                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/activate" element={<ActivateAccount />} />

                {/* Private Routes (Sidebar will always be present) */}
                <Route element={<SidebarLayout />}>
                    <Route path="/subscription" element={<PricePlans />} />
                    <Route path="/menus" element={<MenuList />} />
                    <Route path="/api-access" element={<AdminApiDocs />} />
                    <Route path="/profile" element={<ProtectedRoute><ProfileSettings /></ProtectedRoute>} />
                    <Route path="/chatbot-embed" element={<ChatBotEmbed restaurantId={restaurantId} />} />
                    <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                    <Route path="/theme-setup" element={<ChatbotThemeCustomizer restaurantId={restaurantId} />} />
                    <Route path="/analytics" element={<ChatAnalyticsDashboard restaurantId={restaurantId} />} />
                </Route>
                <Route path="/admin/login" element={<AdminLogin />} />
                <Route path="/admin/subscriptions" element={<ManageSubscriptions />} />
                <Route path="/admin/dashboard" element={<PrivateRoute element={<AdminDashboard />} />} />
                <Route path="/admin/restaurants" element={<ManageRestaurants />} />
                <Route path="/admin/users" element={<AdminUsers />} />
                <Route path="/admin/chat-limit" element={<AdminChatLimit />} />
                <Route path="/admin/chat-resets" element={<AdminChatResetLogs />} />
                <Route path="/admin/newsletter" element={<AdminNewsletter />} />
                <Route path="/chatbot-ui" element={<ChatbotWrapper />} />
                <Route path="/chatbot-ui/:slug" element={<ChatbotWrapper />} />
                <Route path="/subscription-success" element={<SubscriptionSuccess />} />
                <Route path="/subscription-cancel" element={<SubscriptionCancel />} />
            </Routes>
        </Router>
    );
}

export default App;
